/**
 * AccountPresenceContext — live "who's online in my account" via Ably presence.
 *
 * Why a global provider (not a screen hook): presence must be entered for as
 * long as the app is foregrounded, regardless of which screen is mounted — so
 * other members see you online while you're anywhere in the app, and a screen
 * unmounting never yanks your presence. We enter ONCE here (high in the tree)
 * and expose the live member list; the Home "Online now" rail just reads it.
 *
 * Channel: `presence_${stage}_account_${accountId}` — account-scoped, so the
 * member set is the people on YOUR account (caregiver team / partner staff),
 * which is exactly who DMs are openable with (DMs require a shared project).
 *
 * clientId encoding mirrors get-ably-token: `${accountId}_${cognitoId}`. We
 * strip the `${accountId}_` prefix so callers see bare cognito_ids, and dedup
 * by cognito_id (Ably returns one entry per CONNECTION, not per user).
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { AppState } from 'react-native';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { useAblyClient } from './AblyContext';
import config from '../config';

export interface OnlineMember {
  cognitoId: string;
  firstName?: string | null;
  hasAvatar?: boolean;
}

const AccountPresenceContext = createContext<{ members: OnlineMember[] }>({ members: [] });
export const useAccountPresence = () => useContext(AccountPresenceContext);

function accountPresenceChannel(stage: string, accountId: string): string {
  return `presence_${stage}_account_${accountId}`;
}

function bareCognitoId(clientId: string, accountId?: string | null): string {
  if (!clientId || !accountId) return clientId;
  const prefix = `${accountId}_`;
  return clientId.startsWith(prefix) ? clientId.slice(prefix.length) : clientId;
}

export const AccountPresenceProvider = ({ children }: { children: React.ReactNode }) => {
  const { client } = useAblyClient();
  const authorized = useSelector((s: RootState) => s.session.authorized);
  const accountId = useSelector((s: RootState) => (s as any).session?.account?.account_id);
  const user = useSelector((s: RootState) => (s as any).authentication?.user);
  const cognitoId = user?.cognito_id;
  const [members, setMembers] = useState<OnlineMember[]>([]);
  const stage = (config as any)?.env || 'development';

  useEffect(() => {
    if (!client || !authorized || !accountId || !cognitoId) {
      setMembers([]);
      return;
    }
    const channel = client.channels.get(accountPresenceChannel(stage, accountId));
    let cancelled = false;
    // Captured once — name/avatar updates don't re-enter (would burn events).
    const selfData = { first_name: user?.first_name ?? null, has_avatar: !!user?.avatar };

    // Our own Ably clientId (`${accountId}_${sub}`) — authoritative for
    // excluding self. The token's clientId is built from the backend cognito
    // `sub`, which may differ from redux's `authentication.user.cognito_id`
    // (username vs sub), so comparing cognito_ids isn't reliable; compare the
    // raw clientId instead.
    const ownClientId = (client as any)?.auth?.clientId || `${accountId}_${cognitoId}`;

    const refresh = () => {
      try {
        (channel.presence as any).get((err: any, list: any[]) => {
          if (cancelled || err) return;
          const seen = new Map<string, OnlineMember>();
          (list || []).forEach((m: any) => {
            if (m.clientId && m.clientId === ownClientId) return; // skip self
            const cid = bareCognitoId(m.clientId, accountId);
            seen.set(cid, {
              cognitoId: cid,
              firstName: m.data?.first_name ?? null,
              hasAvatar: !!m.data?.has_avatar,
            });
          });
          setMembers(Array.from(seen.values()));
        });
      } catch { /* best-effort — keep prior mirror */ }
    };
    const handler = () => refresh();
    channel.presence.subscribe(['enter', 'leave', 'update'], handler);

    const enter = () => {
      try { (channel.presence as any).enter(selfData, () => refresh()); } catch { refresh(); }
    };
    const leave = () => { try { (channel.presence as any).leave(); } catch { /* disconnect cleans up */ } };

    enter();
    // Leave on background, re-enter on foreground (matches the heartbeat).
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') enter();
      else leave();
    });

    return () => {
      cancelled = true;
      try { channel.presence.unsubscribe(['enter', 'leave', 'update'], handler); } catch { /* ignore */ }
      leave();
      sub.remove();
    };
    // selfData fields intentionally excluded from deps — see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, authorized, accountId, cognitoId, stage]);

  return (
    <AccountPresenceContext.Provider value={{ members }}>
      {children}
    </AccountPresenceContext.Provider>
  );
};

export default AccountPresenceContext;
